import * as THREE from 'three';
/**
 * The Random class provides utility methods for generating various types of random values,
 * including integers, floating-point numbers, bits, and colors.
 */
export class Random {
  /**
   * Generates a random integer between min (inclusive) and max (inclusive).
   * @param {number} min - The minimum value (inclusive).
   * @param {number} max - The maximum value (inclusive).
   * @returns {number} A random integer between min and max.
   */
  static integer(min, max) {
    if (min > max) {
      throw new Error("Min value must be less than or equal to max value.");
    }
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Generates a random floating-point number between min (inclusive) and max (exclusive).
   * @param {number} min - The minimum value (inclusive).
   * @param {number} max - The maximum value (exclusive).
   * @param {number} [precision=2] - The number of decimal places (optional).
   * @returns {number} A random floating-point number between min and max.
   */
  static float(min, max, precision = 2) {
    if (min > max) {
      throw new Error("Min value must be less than or equal to max value.");
    }
    const factor = 10 ** precision; // Determine the factor for precision
    return Math.round((Math.random() * (max - min) + min) * factor) / factor;
  }

  /**
   * Generates a random bit (0 or 1).
   * @returns {number} A random bit (0 or 1).
   */
  static bit() {
    return Math.random() < 0.5 ? 0 : 1;
  }

  /**
   * Generates a random color optionally in hexadecimal format.
   * @param {boolean} [hex=false] - If true, returns the color in hexadecimal format; otherwise, returns a THREE.Color object.
   */
  static color(hex = false) {
    if (hex === true) {
      // Returns a random color in hexadecimal format
      return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
    }
    return new THREE.Color(Math.random(), Math.random(), Math.random());
  }

  /**
   *
   * @param {*} min
   * @param {*} max
   * @returns
   */
  static vector3(min = -1, max = 1, float = false) {
    return {
      x: float ? Random.float(min, max) : Random.integer(min, max),
      y: float ? Random.float(min, max) : Random.integer(min, max),
      z: float ? Random.float(min, max) : Random.integer(min, max),
    };
  }

  /**
   *
   * @param {*} min
   * @param {*} max
   * @param {*} integer
   * @returns
   */
  static vector2(min = -1, max = 1, float = false) {
    return {
      x: float ? Random.float(min, max) : Random.integer(min, max),
      y: float ? Random.float(min, max) : Random.integer(min, max),
    };
  }
}
